import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    getSitOnFloorY,
    getWallTopY,
} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import {getDefaultUpperCabinetOriginY} from 'components/customer/RoomPlanner/helpers/calculatePositionY';
import {
    isHangingLayoutItem,
    isRangehoodPlaceholder,
} from 'components/customer/RoomPlanner/helpers/placeholder/style';

export {isRangehoodPlaceholder};

/**
 * Fallback only when hanging next to an existing run is impossible.
 * Default hang matches a normal 550mm wall cabinet underside (1550mm
 * floor-distance), not 900+600 rangehood clearance (that sat 5–50mm lower).
 */
export const RANGEHOOD_CLEARANCE_ABOVE_BENCH_MM = 600;
export const DEFAULT_BENCHTOP_HEIGHT_MM = 900;

/**
 * Lowest underside among wall cabinets and hanging placeholders, so a new
 * upper lines up with that run. Null when the room has neither.
 * @param {CabinetMesh[]} items Cabinets and placeholders already in the room
 * @return {number | null} World Y of the underside, or null
 */
const getAlignedWallRunUndersideY = (items: CabinetMesh[]): number | null => {
    let underside: number | null = null;

    for (const item of items) {
        if (item.userData?.isTempMesh) continue;
        if (!isHangingLayoutItem(item)) continue;

        item.updateMatrixWorld(true);
        // Cabinets report floor-distance from `position.y`; placeholders from
        // AABB min.y. Align both to that same underside so 1550 matches 1550.
        const undersideY = item.userData?.isPlaceholder
            ? computeWorldBoundsExcludingAnimatedDoorParts(item).min.y
            : item.position.y;
        if (!Number.isFinite(undersideY)) continue;

        if (underside == null || undersideY < underside) {
            underside = undersideY;
        }
    }

    return underside;
};

/**
 * World Y for the underside of a newly placed upper placeholder.
 * Prefers lining up with wall cabinets; otherwise the default 550mm upper
 * cabinet origin (same 1550mm floor-distance as a normal wall cabinet).
 * @param {number} wallHeight Wall height in scene units
 * @param {CabinetMesh[]} existingItems Cabinets and placeholders in the room
 * @return {number} Target world Y of the mesh bottom
 */
export const getRangehoodUndersideY = (
    wallHeight: number,
    existingItems: CabinetMesh[] = []
): number => {
    const aligned = getAlignedWallRunUndersideY(existingItems);
    if (aligned != null) return aligned;

    return getDefaultUpperCabinetOriginY(wallHeight);
};

/**
 * Move the group so its mesh bottom sits on targetBottomY, clamped to the room.
 * @param {CabinetMesh} group Placeholder mesh
 * @param {number} targetBottomY Desired world Y of the mesh underside
 * @return {void}
 */
export const seatPlaceholderMeshBottom = (
    group: CabinetMesh,
    targetBottomY: number
): void => {
    group.updateMatrixWorld(true);
    const box = computeWorldBoundsExcludingAnimatedDoorParts(group);
    if (
        box.isEmpty() ||
        !Number.isFinite(box.min.y) ||
        !Number.isFinite(box.max.y)
    ) {
        return;
    }

    const meshHeight = box.max.y - box.min.y;
    const wallHeight =
        typeof group.userData.wallHeight === 'number'
            ? group.userData.wallHeight
            : getRoomDimensionsComputed().wallHeight;
    const floorY = getSitOnFloorY(wallHeight);
    const ceilingY = getWallTopY(wallHeight);
    const maxBottom = ceilingY - meshHeight;
    const clampedBottom =
        maxBottom < floorY
            ? floorY
            : Math.max(floorY, Math.min(targetBottomY, maxBottom));

    group.position.y += clampedBottom - box.min.y;
    group.updateMatrixWorld(true);
};

/**
 * Seat a base placeholder on the floor, or hang an upper (style=3) like a
 * rangehood / wall cabinet.
 * @param {CabinetMesh} group Placeholder that was just loaded or spawned
 * @param {CabinetMesh[]} existingItems Cabinets and placeholders already in the room
 * @return {void}
 */
export const applyPlaceholderVerticalPlacement = (
    group: CabinetMesh,
    existingItems: CabinetMesh[] = []
): void => {
    const wallHeight =
        typeof group.userData.wallHeight === 'number'
            ? group.userData.wallHeight
            : getRoomDimensionsComputed().wallHeight;
    const others = existingItems.filter(
        (item) => item !== group && item.uuid !== group.uuid
    );

    if (isHangingLayoutItem(group)) {
        seatPlaceholderMeshBottom(
            group,
            getRangehoodUndersideY(wallHeight, others)
        );
        return;
    }

    seatPlaceholderMeshBottom(group, getSitOnFloorY(wallHeight));
};
